import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { composeValidators, maxLengthValidation, noHtmlPatternValidation, requiredValidation } from 'validators';
import { Checkbox, Spinner, Callout, ConfirmationModal, TextField, Typography } from 'seagull';
import { getCustomMessageValidation } from '~/business-logic/validators';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { useRestfulWrapper } from '../useRestfulWrapper';
import styles from './AgreementTerminationModal.module.scss';
const emailContentLabel = 'Email content';
const emailSubjectLabel = 'Email subject';
const emailContentRules = getCustomMessageValidation(emailContentLabel, true);
const emailSubjectRules = composeValidators([
    requiredValidation('Required'),
    maxLengthValidation(120, emailSubjectLabel),
    noHtmlPatternValidation(emailSubjectLabel)
]);
function getDefaultEmailContent(buyerFirstName) {
    return "Dear ".concat(buyerFirstName, ",\n\nI am writing to formally notify you that, as per our previous discussions, I am terminating the agreement between us effective immediately.\n\nPlease note that all outstanding tasks and responsibilities have been completed to the best of our ability, and we're available to provide any necessary assistance during this transition period.\n\nWe sincerely appreciate the opportunity to have worked with you and wish you all the best in your future endeavors.");
}
function getDefaultEmailSubject(vendorName) {
    return "Your agreement with ".concat(vendorName, " was terminated");
}
function validateCustomerName(value, relationship) {
    const isValid = relationshipModel.customerName(relationship).trim().toLowerCase() === value.trim().toLowerCase();
    return isValid || "Company name doesn't match";
}
export function AgreementTerminationModal(props) {
    const { t: texts } = useLocaleKeys('hooks.terminateAgreement');
    const { t: commonTexts } = useLocaleKeys('common');
    const { relationshipId, dismissModal } = props;
    const { api } = useStoreActions();
    const [notifyClient, setNotifyClient] = useState(false);
    const { control, setValue, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            companyName: '',
            emailSubject: '',
            emailContent: ''
        }
    });
    const { data, loading, renderRestfulState } = useRestfulWrapper({
        fetch: ()=>api.relationship.get(relationshipId)
    });
    useEffect(()=>{
        if (data) {
            setValue('emailSubject', getDefaultEmailSubject(relationshipModel.vendorName(data)));
            setValue('emailContent', getDefaultEmailContent(relationshipModel.buyerFirstName(data) || ''));
        }
    }, [
        data
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: texts.title(),
        skin: "danger",
        textAlignCenter: false,
        primaryButtonLabel: notifyClient ? commonTexts.buttons.terminateAndNotify() : commonTexts.buttons.terminate(),
        primaryButtonDisabled: loading,
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onPrimaryButtonClick: handleSubmit((param)=>{
            let { emailSubject, emailContent } = param;
            return dismissModal({
                confirmed: true,
                notification: notifyClient ? {
                    subject: emailSubject,
                    message: emailContent
                } : undefined
            });
        }),
        onSecondaryButtonClick: ()=>dismissModal({
                confirmed: false
            }),
        content: renderRestfulState({
            renderLoader: ()=>/*#__PURE__*/ _jsx("div", {
                    className: styles.loading,
                    children: /*#__PURE__*/ _jsx(Spinner, {})
                }),
            renderChild: (param)=>{
                let { data: relationship } = param;
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.modalContentWrapper,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsxs(Typography, {
                                    children: [
                                        "Terminating the agreement with",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.customerName,
                                            as: "span",
                                            weight: "bold",
                                            children: relationshipModel.customerName(relationship)
                                        }),
                                        ' ',
                                        "will immediately stop new invoices issuance and the initiation of payments for pending invoices."
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: texts.notePaymentsInProcess()
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: texts.ensureFinalInvoicesSettled()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Controller, {
                            name: "companyName",
                            control: control,
                            rules: {
                                validate: (value)=>validateCustomerName(value, relationship)
                            },
                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                    collapseHelperText: false
                                }, connectControllerToTextField(p)), {
                                    label: texts.confirmLabel(),
                                    testId: "company-name-verification"
                                }))
                        }),
                        /*#__PURE__*/ _jsxs(Callout, {
                            children: [
                                texts.learnMoreCallout(),
                                ' ',
                                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                    openInNewTab: true,
                                    href: helpArticles.howToEditAgreement(),
                                    children: texts.learnMoreLink()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.notificationEmailFields,
                            children: [
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    testId: "toggle-notify",
                                    onChange: ()=>setNotifyClient((prev)=>!prev),
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: texts.notifyClient()
                                    })
                                }),
                                notifyClient && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            rules: emailSubjectRules,
                                            control: control,
                                            name: "emailSubject",
                                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    collapseHelperText: false,
                                                    label: emailSubjectLabel,
                                                    testId: "email-subject"
                                                }, connectControllerToTextField(p, {
                                                    alwaysShowErrors: true
                                                })))
                                        }),
                                        /*#__PURE__*/ _jsx(Controller, {
                                            rules: emailContentRules,
                                            control: control,
                                            name: "emailContent",
                                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    collapseHelperText: false,
                                                    multiline: true,
                                                    minRows: 10,
                                                    maxRows: 10,
                                                    label: emailContentLabel,
                                                    testId: "email-content"
                                                }, connectControllerToTextField(p)))
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                });
            }
        })
    });
}
